import React, { useState, useEffect } from 'react';
import {
    Row, Col, Card, Table, Badge, Button,
    Form
} from 'react-bootstrap';
import {
    History, Package, FileText, Save, Filter, ChevronUp, ChevronDown
} from 'lucide-react';
import Cookies from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { ApprovelFilterData, GetOrderApprovalData, PostOrderApproval } from '../../Redux/Slice/OrderApprovel/OrderApprovalSlice';
import { toast, ToastContainer } from 'react-toastify';

const OrderApproval = () => {
    const dispatch = useDispatch();
    const isdark = Cookies.get("theme") || "light";
    const { filterData, poList, orderDetails: apiOrderDetails, historyData, loading: poLoading, detailsLoading } = useSelector((state) => state.OrderApprovalSlice);

    // Calculate current financial year dates
    const today = new Date();
    const currentYear = today.getFullYear();
    const fiscalYearStart = today.getMonth() >= 3 ? `${currentYear}-04-01` : `${currentYear - 1}-04-01`;
    const fiscalYearEnd = today.getMonth() >= 3 ? `${currentYear + 1}-03-31` : `${currentYear}-03-31`;

    const [filters, setFilters] = useState({
        FirmID: '',
        PartyID: '',
        FromDate: fiscalYearStart,
        ToDate: fiscalYearEnd,
        Status: '3' // 3 = All
    });

    const [showFilters, setShowFilters] = useState(false);

    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]); // Array of indices
    const [orderDetails, setOrderDetails] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        dispatch(ApprovelFilterData());
    }, [dispatch]);

    useEffect(() => {
        if (apiOrderDetails && selectedOrder) {
            // Filter details by SoudaID matching the selectedOrder ID
            const filtered = apiOrderDetails.filter(item =>
                String(item.SoudaID) === String(selectedOrder) ||
                String(item.OrderID) === String(selectedOrder)
            );
            setOrderDetails(filtered.map(item => ({
                ...item,
                remark: item.RejectRemark || '',
                status: item.Approved === 'Approve' ? 'approve' : item.Approved === 'Reject' ? 'reject' : 'pending'
            })));
        }
    }, [apiOrderDetails, selectedOrder]);



    // --- Styles ---
    const styles = {
        main: {
            background: isdark === 'light' ? '#f4f7fa' : '#020617',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: "'Inter', sans-serif",
            position: 'relative',
        },
        filterSection: {
            background: isdark === 'light' ? '#ffffff' : '#0f172a',
            padding: '12px 24px',
            borderBottom: `1px solid ${isdark === 'light' ? '#e2e8f0' : '#1e293b'}`,
            position: 'sticky',
            top: 0,
            zIndex: 90,
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
        },
        gridContainer: {
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '12px',
            paddingBottom: '80px',
            minHeight: 0,
            WebkitOverflowScrolling: 'touch',
        },
        masterCard: {
            height: '100%',
            minHeight: '400px',
            background: isdark === 'light' ? '#ffffff' : '#0f172a',
            borderRadius: '12px',
            border: `1px solid ${isdark === 'light' ? '#e2e8f0' : '#1e293b'}`,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        },
        tableWrapper: {
            flex: 1,
            overflowY: 'auto',
            overflowX: 'auto',
        },
        table: {
            fontSize: '13px',
            marginBottom: 0,
        },
        stickyHeader: {
            position: 'sticky',
            top: 0,
            background: isdark === 'light' ? '#f8fafc' : '#1e293b',
            zIndex: 10,
            borderBottom: `2px solid ${isdark === 'light' ? '#e2e8f0' : '#334155'}`,
        },
        rowActive: {
            background: '#3B82F6 !important',
            borderLeft: '4px solid #2563eb',
            color: '#ffffff !important',
            fontWeight: '600',
        },
        statusBadge: (status) => ({
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.02em',
            backgroundColor:
                status === 'Approved' ? 'rgba(16, 185, 129, 0.1)' :
                    status === 'Rejected' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
            color:
                status === 'Approved' ? '#10b981' :
                    status === 'Rejected' ? '#ef4444' : '#f59e0b',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
        }),
        actionFooter: {
            background: 'transparent',
            padding: '20px',
            paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 9999,
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            pointerEvents: 'none',
        },
        input: {
            fontSize: '13px',
            borderRadius: '6px',
            border: `1px solid ${isdark === 'light' ? '#cbd5e1' : '#334155'}`,
            background: isdark === 'light' ? '#ffffff' : '#1e293b',
            color: isdark === 'light' ? '#0f172a' : '#f8fafc',
        }
    };

    const format2Dec = (val) => {
        if (val === undefined || val === null || val === '') return '0.00';
        const clean = typeof val === 'string' ? val.replace(/,/g, '') : val;
        const num = Number(clean);
        return isNaN(num) ? '0.00' : num.toFixed(2);
    };

    const handlePreview = () => {
        if (!filters.FirmID) {
            toast.warning('Please select a Firm first');
            return;
        }

        const startYear = new Date(filters.FromDate).getFullYear();
        const endYear = new Date(filters.ToDate).getFullYear();
        const dynamicYear = `${startYear}${endYear}`;

        dispatch(GetOrderApprovalData({
            ...filters,
            Year: dynamicYear,
            Option: '0',
            SqlId: Cookies.get('SqlId'),
            AcYearIDs: Cookies.get('FinancialYear')
        }));
    };

    const handleOrderSelect = (order) => {
        setSelectedOrder(order.SoudaID || order.OrderID || order.id);
        setSelectedItem(null);
        setSelectedItems([]);
    };

    const handleItemStatusChange = (index, value) => {
        const newDetails = [...orderDetails];
        newDetails[index].status = value;
        setOrderDetails(newDetails);
    };

    const handleRemarkChange = (index, value) => {
        const newDetails = [...orderDetails];
        newDetails[index].remark = value;
        setOrderDetails(newDetails);
    };

    const handleBulkAction = (action) => {
        if (selectedItems.length === 0) {
            toast.warning('Please select items first');
            return;
        }
        setOrderDetails(prev => prev.map((item, idx) =>
            selectedItems.includes(idx) ? { ...item, status: action } : item
        ));
    };

    const toggleSelectAll = () => {
        if (selectedItems.length === orderDetails.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(orderDetails.map((_, i) => i));
        }
    };

    const toggleSelectItem = (idx) => {
        setSelectedItems(prev =>
            prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
        );
    };

    const handleSubmit = async () => {
        if (!selectedOrder) {
            toast.warn("Please select an order first");
            return;
        }

        const selectedData = orderDetails.filter((_, idx) => selectedItems.includes(idx));
        
        if (selectedData.length === 0) {
            toast.warning("Please select at least one item to process");
            return;
        }

        // Validation: Remark is mandatory for rejection
        const rejectedWithoutRemark = selectedData.filter(item => 
            item.status === 'reject' && (!item.remark || item.remark.trim() === '')
        );

        if (rejectedWithoutRemark.length > 0) {
            toast.error(`Remark is mandatory for rejected items: ${rejectedWithoutRemark.map(i => i.ItemName).join(', ')}`);
            return;
        }

        // Prepare the array of items for bulk processing
        const payloadItems = selectedData.map(item => ({
            SoudaDetailID: item.SoudaDetailID,
            SoudaID: item.SoudaID,
            Status: item.status === 'approve' ? '1' : item.status === 'reject' ? '2' : '0',
            Remark: item.remark
        })).filter(item => item.Status !== '0');

        

        if (payloadItems.length === 0) {
            toast.warning("No status changes to process (select Approve or Reject)");
            return;
        }

        try {
            setLoading(true);
            
            await dispatch(PostOrderApproval({
                DataBase: Cookies.get('Database'),
                Items: payloadItems,
                SqlId: Cookies.get('SqlId'),
                AcYearIDs: Cookies.get('FinancialYear')
            })).unwrap();

            toast.success('Batch selection processed successfully!');
            handlePreview();
            setSelectedItems([]);
            setSelectedOrder(null);
            setOrderDetails([]);
            setSelectedItem(null);
        } catch (err) {
            toast.error(err?.message || "Failed to process batch selection");
        } finally {
            setLoading(false);
        }
    };

    // Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey && e.key.toLowerCase() === 'a') {
                e.preventDefault();
                alert('Approve All Triggered');
            }
            if (e.ctrlKey && e.key.toLowerCase() === 'r') {
                e.preventDefault();
                alert('Reset Triggered');
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    return (
        <div style={styles.main}>
            {/* --- STICKY FILTER PANEL --- */}
            <div style={styles.filterSection}>
                <div className="d-flex justify-content-between align-items-center w-100" style={{ marginBottom: showFilters ? '16px' : '0' }}>
                    <div 
                        className="d-flex align-items-center gap-2 text-primary fw-bold cursor-pointer" 
                        onClick={() => setShowFilters(!showFilters)}
                        style={{ userSelect: 'none' }}
                    >
                        <Filter size={16} />
                        <span style={{ fontSize: '13px', letterSpacing: '0.5px' }}>FILTERS</span>
                        {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                </div>
                
                {showFilters && (
                    <div className="d-flex align-items-center flex-wrap gap-3">
                        <div style={{ width: '150px' }}>
                            <Form.Label className="mb-1 small fw-bold text-muted">FIRM</Form.Label>
                            <Form.Select
                                size="sm"
                                style={styles.input}
                                value={filters.FirmID}
                                onChange={(e) => setFilters({ ...filters, FirmID: e.target.value })}
                            >
                                <option value="">All Firms</option>
                                {filterData?.Firm?.map((firm, idx) => (
                                    <option key={idx} value={firm.FirmID}>{firm.FirmName}</option>
                                ))}
                            </Form.Select>
                        </div>
                        <div style={{ width: '150px' }}>
                            <Form.Label className="mb-1 small fw-bold text-muted">PARTY</Form.Label>
                            <Form.Select
                                size="sm"
                                style={styles.input}
                                value={filters.PartyID}
                                onChange={(e) => setFilters({ ...filters, PartyID: e.target.value })}
                            >
                                <option value="">All Parties</option>
                                {filterData?.Party?.map((party, idx) => (
                                    <option key={idx} value={party.AcID}>{party.AcName}</option>
                                ))}
                            </Form.Select>
                        </div>
                        <div>
                            <Form.Label className="mb-1 small fw-bold text-muted">FROM DATE</Form.Label>
                            <Form.Control
                                type="date"
                                size="sm"
                                style={styles.input}
                                value={filters.FromDate}
                                onChange={(e) => setFilters({ ...filters, FromDate: e.target.value })}
                            />
                        </div>
                        <div>
                            <Form.Label className="mb-1 small fw-bold text-muted">TO DATE</Form.Label>
                            <Form.Control
                                type="date"
                                size="sm"
                                style={styles.input}
                                value={filters.ToDate}
                                onChange={(e) => setFilters({ ...filters, ToDate: e.target.value })}
                            />
                        </div>
                        <div className="d-flex align-items-end flex-wrap gap-1 mt-4">
                            {[
                                { label: 'All', value: '3' },
                                { label: 'Pending', value: '0' },
                                { label: 'Approved', value: '1' },
                                { label: 'Rejected', value: '2' }
                            ].map(st => (
                                <Button
                                    key={st.value}
                                    variant={filters.Status === st.value ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    style={{ borderRadius: '20px', fontSize: '11px', height: '31px', padding: '0 12px' }}
                                    onClick={() => setFilters({ ...filters, Status: st.value })}
                                >
                                    {st.label}
                                </Button>
                            ))}
                        </div>
                        <div className="d-flex align-items-end gap-2 h-100 mt-4">
                            <Button
                                variant="primary"
                                size="sm"
                                className="px-4 fw-bold shadow-sm"
                                style={{ height: '31px' }}
                                onClick={handlePreview}
                            >
                                {poLoading ? '...' : 'PREVIEW'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* --- MAIN GRID --- */}
            <div style={styles.gridContainer}>
                <Row className="h-100 g-3">
                    {/* LEFT: MASTER LIST */}
                    <Col lg={4} className="h-100">
                        <Card style={styles.masterCard}>
                            <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold d-flex align-items-center">
                                    <FileText size={16} className="me-2 text-primary" />
                                    PURCHASE ORDERS
                                </h6>
                                <Badge bg="primary">{poList?.length || 0}</Badge>
                            </div>
                            <div style={styles.tableWrapper}>
                                <Table hover style={styles.table} className="text-nowrap">
                                    <thead style={styles.stickyHeader}>
                                        <tr>
                                            <th>ORDER NO</th>
                                            <th>DATE</th>
                                            <th>BOOK</th>
                                            <th>PARTY NAME</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {poList?.map((po, idx) => (
                                            <tr
                                                key={idx}
                                                onClick={() => handleOrderSelect(po)}
                                                style={selectedOrder === (po.SoudaID || po.OrderID || po.id) ? styles.rowActive : { cursor: 'pointer' }}
                                                className="transition-all"
                                            >
                                                <td style={{ color: selectedOrder === (po.SoudaID || po.OrderID || po.id) ? '#3B82F6' : 'inherit' }} className="fw-bold">{po.OrderNo || po.POrderNo || po.id}</td>
                                                <td style={{ color: selectedOrder === (po.SoudaID || po.OrderID || po.id) ? '#3B82F6' : 'inherit' }}>{po.OrderDate ? new Date(po.OrderDate).toLocaleDateString() : (po.POrderDate || po.date)}</td>
                                                <td style={{ color: selectedOrder === (po.SoudaID || po.OrderID || po.id) ? '#3B82F6' : 'inherit' }}>{po.BookTypeName}</td>
                                                <td style={{ color: selectedOrder === (po.SoudaID || po.OrderID || po.id) ? '#3B82F6' : 'inherit' }}>{po.Party || po.AcName || po.party}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card>
                    </Col>

                    {/* RIGHT: DETAILS & HISTORY */}
                    <Col lg={8} className="h-100 d-flex flex-column gap-3">
                        {/* ITEM APPROVAL GRID */}
                        <Card style={{ ...styles.masterCard, flex: 1.5, position: 'relative' }}>
                            {(loading || detailsLoading) && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    background: 'rgba(255,255,255,0.6)',
                                    zIndex: 100,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backdropFilter: 'blur(2px)'
                                }}>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            )}
                            <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold d-flex align-items-center">
                                    <Package size={16} className="me-2 text-primary" />
                                    ITEM APPROVAL DETAILS {selectedOrder && poList.find(p => String(p.SoudaID || p.OrderID || p.id) === String(selectedOrder)) &&
                                        `- ${poList.find(p => String(p.SoudaID || p.OrderID || p.id) === String(selectedOrder)).OrderNo} (${new Date(poList.find(p => String(p.SoudaID || p.OrderID || p.id) === String(selectedOrder)).OrderDate).toLocaleDateString()})`}
                                </h6>
                                <div className="d-flex gap-2">
                                    <Button variant="outline-primary" size="sm" onClick={() => handleBulkAction('approve')}>BULK APPROVE</Button>
                                    <Button variant="outline-danger" size="sm" onClick={() => handleBulkAction('reject')}>BULK REJECT</Button>
                                </div>
                            </div>
                            <div style={styles.tableWrapper}>
                                <Table bordered style={styles.table} className="text-nowrap border-top-0">
                                    <thead style={styles.stickyHeader}>
                                        <tr>
                                            <th className="text-center" style={{ width: '40px' }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={orderDetails.length > 0 && selectedItems.length === orderDetails.length}
                                                    onChange={toggleSelectAll}
                                                />
                                            </th>
                                            <th className="text-center" style={{ width: '95px', minWidth: '90px' }}>ACTION</th>
                                            <th style={{ width: '100px', minWidth: '90px', maxWidth: '100px' }}>REMARK</th>
                                            <th>ITEM NAME</th>
                                            <th>UNIT</th>
                                            <th className="text-end">TAXABLE AMT</th>
                                            <th className="text-end">FINAL AMT</th>
                                            <th className="text-end">SODA ON</th>
                                            <th className="text-end">PCS</th>
                                            <th className="text-end">QTY</th>
                                            <th className="text-end">RATE</th>
                                            <th className="text-center">CGST%</th>
                                            <th className="text-end">CGST AMT</th>
                                            <th className="text-center">SGST%</th>
                                            <th className="text-end">SGST AMT</th>
                                            <th className="text-center">IGST%</th>
                                            <th className="text-end">IGST AMT</th>
                                            <th className="text-center">DISC%</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orderDetails.map((item, idx) => (
                                            <tr
                                                key={idx}
                                                style={{
                                                    transition: 'background 0.3s',
                                                    cursor: 'pointer',
                                                    background: selectedItem === idx ? '#3B82F6' : 'transparent',
                                                    borderLeft: selectedItem === idx ? '4px solid #2563eb' : 'none',
                                                    color: selectedItem === idx ? '#ffffff' : 'inherit',
                                                    fontWeight: selectedItem === idx ? '600' : 'normal'
                                                }}
                                                onClick={() => setSelectedItem(idx)}
                                            >
                                                <td className="text-center">
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={selectedItems.includes(idx)}
                                                        onChange={() => toggleSelectItem(idx)}
                                                    />
                                                </td>
                                                 <td className="text-center" style={{ width: '95px', minWidth: '90px' }}>
                                                    <Form.Select
                                                        size="sm"
                                                        className="py-0 px-1"
                                                        style={{
                                                            fontSize: '12px',
                                                            height: '24px',
                                                            border: item.status === 'reject' ? '1px solid #ef4444' : item.status === 'approve' ? '1px solid #10b981' : styles.input.border
                                                        }}
                                                        value={item.status}
                                                        onChange={(e) => handleItemStatusChange(idx, e.target.value)}
                                                    >
                                                        <option value="pending">Pending</option>
                                                        <option value="approve" className="text-success fw-bold">Approve</option>
                                                        <option value="reject" className="text-danger fw-bold">Reject</option>
                                                    </Form.Select>
                                                </td>
                                                <td style={{ width: '100px', minWidth: '90px', maxWidth: '100px' }}>
                                                    <Form.Control
                                                        size="sm"
                                                        placeholder={item.status === 'reject' ? "Reason..." : "Remark..."}
                                                        value={item.remark}
                                                        onChange={(e) => handleRemarkChange(idx, e.target.value)}
                                                        title={item.remark || (item.status === 'reject' ? "Reason required" : "Remark")}
                                                        style={{
                                                            ...styles.input,
                                                            width: '100px',
                                                            maxWidth: '100px',
                                                            height: '24px',
                                                            fontSize: '11px',
                                                            padding: '2px 6px',
                                                            borderColor: item.status === 'reject' && !item.remark ? '#ef4444' : styles.input.borderColor,
                                                            backgroundColor: item.status === 'reject' && !item.remark ? 'rgba(239, 68, 68, 0.05)' : styles.input.background
                                                        }}
                                                    />
                                                </td>
                                                <td className="fw-bold" style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{item.ItemName || item.item}</td>
                                                <td style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{item.Unit || item.UnitName || item.unit}</td>
                                                <td className="text-end fw-bold" style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{format2Dec(item.TaxableAmount)}</td>
                                                <td className="text-end fw-bold" style={{ color: selectedItem === idx ? '#3B82F6' : '#10b981' }}>{format2Dec(item.Amount)}</td>
                                                <td className="text-end" style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{item.SodaOn || item.soda}</td>
                                                <td className="text-end" style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{item.Pcs || item.pcs || '0'}</td>
                                                <td className="text-end fw-bold" style={{ color: selectedItem === idx ? (isdark === 'light' ? '#3B82F6' : '#3B82F6') : (isdark === 'light' ? '#3b82f6' : '#60a5fa') }}>{Array.isArray(item.Qty) ? item.Qty[0] : (item.Qty || item.qty)}</td>
                                                <td className="text-end" style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{item.Rate || item.rate}</td>
                                                <td className="text-center" style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{item.CGSTPer || '0'}%</td>
                                                <td className="text-end" style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{item.CGSTAmt || '0.00'}</td>
                                                <td className="text-center" style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{item.SGSTPer || '0'}%</td>
                                                <td className="text-end" style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{item.SGSTAmt || '0.00'}</td>
                                                <td className="text-center" style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{item.IGSTPer || '0'}%</td>
                                                <td className="text-end" style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{item.IGSTAmt || '0.00'}</td>
                                                <td className="text-center" style={{ color: selectedItem === idx ? '#3B82F6' : 'inherit' }}>{item.DiscPer || '0'}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card>

                        {/* SUPPLIER RATE HISTORY */}
                        <Card style={{ ...styles.masterCard, flex: 1 }}>
                            <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold d-flex align-items-center">
                                    <History size={16} className="me-2 text-primary" />
                                    VENDOR RATE HISTORY
                                </h6>
                            </div>
                            <div style={styles.tableWrapper}>
                                <Table striped bordered style={styles.table} className="text-nowrap border-top-0">
                                    <thead style={styles.stickyHeader}>
                                        <tr>
                                            <th>DATE</th>
                                            <th>SUPPLIER NAME</th>
                                            <th className="text-end">QTY</th>
                                            <th className="text-end">RATE</th>
                                            <th className="text-end">AMOUNT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedItem !== null && historyData && historyData
                                            .filter(h => h.ItemID === orderDetails[selectedItem]?.ItemID)
                                            .map((h, hIdx) => (
                                                <tr key={hIdx}>
                                                    <td>{h.Date ? new Date(h.Date).toLocaleDateString('en-GB') : 'N/A'}</td>
                                                    <td>{h.SupplierName || 'N/A'}</td>
                                                    <td className="text-end">{h.Qty || '0'}</td>
                                                    <td className="text-end fw-bold text-success">{h.SupplierRate || h.SupplierRate || '0.00'}</td>
                                                    <td className="text-end">{h.SupplierAmt}</td>
                                                </tr>
                                            ))
                                        }
                                        {selectedItem !== null && (!historyData || historyData.filter(h => h.ItemID === orderDetails[selectedItem]?.ItemID).length === 0) && (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted py-3">No purchase history found for this item</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* --- ACTION FOOTER --- */}
            <div style={styles.actionFooter}>
                <Button
                    variant="success"
                    className="px-5 fw-bold d-flex align-items-center gap-2 shadow-lg"
                    onClick={handleSubmit}
                    style={{
                        borderRadius: '30px',
                        background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
                        border: 'none',
                        pointerEvents: 'auto'
                    }}
                >
                    <Save size={18} /> SUBMIT APPROVAL
                </Button>
            </div>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

            {/* --- INLINE GLOBAL CSS FOR ANIMATIONS & SCROLLBAR --- */}
            <style>{`
                .transition-all { transition: all 0.2s ease-in-out; }
                .cursor-pointer { cursor: pointer; }
                
                /* Custom Scrollbar */
                ::-webkit-scrollbar { width: 6px; height: 6px; }
                ::-webkit-scrollbar-track { background: var(--theme-scrollbar-track, transparent); }
                ::-webkit-scrollbar-thumb { 
                    background: var(--theme-scrollbar-thumb, ${isdark === 'light' ? '#cbd5e1' : '#334155'}); 
                    border-radius: 10px; 
                }
                ::-webkit-scrollbar-thumb:hover { background: var(--theme-scrollbar-thumb-hover, #94a3b8); }

                /* Table Hover Effects */
                .table-hover tbody tr:hover {
                    background-color: ${isdark === 'light' ? '#f1f5f9' : '#1e293b'} !important;
                }

                /* Active Indicators */
                .nav-pills .nav-link.active {
                    background-color: #3b82f6 !important;
                }

                /* Form Controls */
                .form-control:focus, .form-select:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
                }

                /* Button Animations */
                .btn { transition: transform 0.1s active, box-shadow 0.2s; }
                .btn:active { transform: scale(0.98); }

                /* Loading Shimmer */
                .shimmer {
                    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                    background-size: 200% 100%;
                    animation: shimmer 1.5s infinite;
                }
                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
            `}</style>
        </div>
    );
};

export default OrderApproval;
